import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  FilterAlt,
  Check,
  EmojiObjects,
  EditNote,
  AccessTime,
  AutorenewRounded,
  PersonAdd,
  RateReview,
  Close,
  Image,
  MoreVert,
} from "@mui/icons-material";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";
import ContentTypeChip from "./ContentTypeChip";

interface ContentCalendarProps {
  contents: any[];
  onContentClick: (content: any) => void;
  onPublishClick: (content: any) => void;
  onSetPublishedClick: (contentId: string) => void;
  onMoveToDraftClick: (contentId: string) => void;
  onAssignClick: (content: any) => void;
  onReviewClick: (content: any) => void;
  onDeleteClick: (contentId: string) => void;
  filters: any;
  onMonthChange?: (startDate: Moment, endDate: Moment) => void;
}

const ContentCalendar: React.FC<ContentCalendarProps> = ({
  contents,
  onContentClick,
  onPublishClick,
  onSetPublishedClick,
  onMoveToDraftClick,
  onAssignClick,
  onReviewClick,
  onDeleteClick,
  filters,
  onMonthChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<any>(null);

  // Notify parent when month changes to update date filters
  useEffect(() => {
    if (onMonthChange) {
      const startOfMonth = currentMonth.clone().startOf("month").startOf("day");
      const endOfMonth = currentMonth.clone().endOf("month").endOf("day");
      onMonthChange(startOfMonth, endOfMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]); // Only depend on currentMonth, onMonthChange is stable

  // Generate calendar grid
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const startDate = startOfMonth.clone().startOf("week");
    const endDate = endOfMonth.clone().endOf("week");

    const days: Moment[] = [];
    let day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, "day");
    }

    return days;
  };

  // Group contents by date
  const getContentsForDay = (day: Moment) => {
    return contents.filter((content) => {
      const contentDate = content.publishDate
        ? moment(content.publishDate)
        : content.created_date
        ? moment(content.created_date)
        : null;

      if (!contentDate) return false;

      return contentDate.isSame(day, "day");
    });
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, "month"));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(currentMonth.clone().add(1, "month"));
  };

  // Go to today
  const goToToday = () => {
    setCurrentMonth(moment());
  };

  // Get status icon for content
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "ideated":
        return <EmojiObjects fontSize="small" />;
      case "draft":
        return <EditNote fontSize="small" />;
      case "published":
        return <Check fontSize="small" />;
      case "generating":
        return <AutorenewRounded fontSize="small" />;
      case "scheduled":
        return <AccessTime fontSize="small" />;
      default:
        return null;
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ideated":
        return "info";
      case "draft":
        return "warning";
      case "published":
        return "primary";
      case "generating":
        return "secondary";
      case "scheduled":
        return "secondary";
      default:
        return "default";
    }
  };

  // Get approval badge
  const getApprovalBadge = (content: any) => {
    if (!content.assignedTo) return null;

    const status = content.assignedTo.status;
    let icon;
    let color: any = "default";

    switch (status) {
      case "approved":
        icon = <Check fontSize="inherit" />;
        color = "success";
        break;
      case "rejected":
        icon = <Close fontSize="inherit" />;
        color = "error";
        break;
      case "in_review":
        icon = <RateReview fontSize="inherit" />;
        color = "warning";
        break;
      case "pending":
        icon = <PersonAdd fontSize="inherit" />;
        color = "default";
        break;
    }

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          bgcolor: `${color}.light`,
          color: `${color}.dark`,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          fontSize: "0.7rem",
          fontWeight: "bold",
        }}
      >
        {icon}
      </Box>
    );
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Calendar Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        <IconButton onClick={previousMonth} size="small">
          <ChevronLeft />
        </IconButton>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <Chip
            label="Today"
            size="small"
            onClick={goToToday}
            sx={{ cursor: "pointer" }}
          />
        </Box>
        <IconButton onClick={nextMonth} size="small">
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1,
          minHeight: "70vh",
        }}
      >
        {/* Week day headers */}
        {weekDays.map((day) => (
          <Box
            key={day}
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              py: 1,
              color: "text.secondary",
            }}
          >
            {day}
          </Box>
        ))}

        {/* Calendar days */}
        {calendarDays.map((day) => {
          const dayContents = getContentsForDay(day);
          const isToday = day.isSame(moment(), "day");
          const isCurrentMonth = day.isSame(currentMonth, "month");

          return (
            <Card
              key={day.format("YYYY-MM-DD")}
              variant="outlined"
              sx={{
                minHeight: 120,
                bgcolor: isToday ? "action.hover" : "background.paper",
                opacity: isCurrentMonth ? 1 : 0.5,
                border: isToday ? 2 : 1,
                borderColor: isToday ? "primary.main" : "divider",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent
                sx={{
                  p: 1,
                  "&:last-child": { pb: 1 },
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Day number */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isToday ? "bold" : "normal",
                      color: isToday ? "primary.main" : "text.primary",
                    }}
                  >
                    {day.format("D")}
                  </Typography>
                  {dayContents.length > 0 && (
                    <Chip
                      label={dayContents.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        bgcolor: "primary.light",
                        color: "primary.dark",
                      }}
                    />
                  )}
                </Box>

                {/* Content items */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  {dayContents.map((content) => (
                    <Tooltip
                      key={content.id}
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {content.title}
                          </Typography>
                          <Typography variant="caption">
                            Type: {content.type}
                          </Typography>
                          <br />
                          <Typography variant="caption">
                            Status: {content.status}
                          </Typography>
                          {content.assignedTo && (
                            <>
                              <br />
                              <Typography variant="caption">
                                Assigned to: {content.assignedTo.email}
                              </Typography>
                            </>
                          )}
                          {content.funnelStage && content.funnelStage !== "N/A" && (
                            <>
                              <br />
                              <Typography variant="caption">
                                Funnel: {content.funnelStage.toUpperCase()}
                              </Typography>
                            </>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Card
                        sx={{
                          p: 0.5,
                          cursor: "pointer",
                          bgcolor: "background.default",
                          borderLeft: 3,
                          borderColor: content.campaignColor || "grey.300",
                          "&:hover": {
                            bgcolor: "action.hover",
                            transform: "scale(1.02)",
                          },
                          transition: "all 0.2s",
                          position: "relative",
                        }}
                        onClick={(e) => {
                          // Don't open editor if clicking on menu button
                          const target = e.target as HTMLElement;
                          if (!target.closest('button')) {
                            onContentClick(content);
                          }
                        }}
                      >
                        {/* Actions menu button */}
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            padding: 0.25,
                            bgcolor: "background.paper",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setSelectedContentId(content.id);
                            setSelectedContent(content);
                          }}
                        >
                          <MoreVert sx={{ fontSize: "0.9rem" }} />
                        </IconButton>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.25,
                          }}
                        >
                          {/* Status icon */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: `${getStatusColor(content.status)}.main`,
                            }}
                          >
                            {getStatusIcon(content.status)}
                          </Box>

                          {/* Content type chip (small) */}
                          <Box sx={{ fontSize: "0.65rem" }}>
                            <ContentTypeChip type={content.type} size="small" />
                          </Box>

                          {/* Approval badge */}
                          {getApprovalBadge(content)}

                          {/* Funnel stage */}
                          {content.funnelStage && content.funnelStage !== "N/A" && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                bgcolor:
                                  content.funnelStage === "tofu"
                                    ? "success.light"
                                    : content.funnelStage === "mofu"
                                    ? "info.light"
                                    : "secondary.light",
                                borderRadius: 1,
                                px: 0.5,
                                fontSize: "0.6rem",
                                fontWeight: "bold",
                                color: "text.primary",
                              }}
                            >
                              <FilterAlt sx={{ fontSize: "0.7rem", mr: 0.25 }} />
                              {content.funnelStage.toUpperCase()}
                            </Box>
                          )}
                        </Box>

                        {/* Title (truncated) */}
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {content.title}
                        </Typography>

                        {/* Images indicator */}
                        {content.images && content.images.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              mt: 0.25,
                            }}
                          >
                            <Image sx={{ fontSize: "0.7rem", color: "text.secondary" }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                color: "text.secondary",
                              }}
                            >
                              {content.images.length}
                            </Typography>
                          </Box>
                        )}

                        {/* Time */}
                        {content.publishDate && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              fontSize: "0.65rem",
                              color: "text.secondary",
                              mt: 0.25,
                            }}
                          >
                            {moment(content.publishDate).format("h:mm a")}
                          </Typography>
                        )}
                      </Card>
                    </Tooltip>
                  ))}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && selectedContentId !== null}
        onClose={(e: any) => {
          e?.stopPropagation();
          setAnchorEl(null);
          setSelectedContentId(null);
          setSelectedContent(null);
        }}
      >
        {selectedContent &&
          ["blog_post", "linkedin_post", "facebook_post", "instagram_post"].includes(selectedContent.type) &&
          selectedContent.status === "draft" && (
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                onPublishClick(selectedContent);
                setAnchorEl(null);
                setSelectedContentId(null);
                setSelectedContent(null);
              }}
            >
              Publish
            </MenuItem>
          )}
        {selectedContent && selectedContent.status === "draft" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onSetPublishedClick(selectedContent.id);
              setAnchorEl(null);
              setSelectedContentId(null);
              setSelectedContent(null);
            }}
          >
            Set as Published
          </MenuItem>
        )}
        {selectedContent && selectedContent.status === "scheduled" && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onMoveToDraftClick(selectedContent.id);
              setAnchorEl(null);
              setSelectedContentId(null);
              setSelectedContent(null);
            }}
          >
            Move to Draft
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onAssignClick(selectedContent);
            setAnchorEl(null);
            setSelectedContentId(null);
            setSelectedContent(null);
          }}
        >
          <PersonAdd sx={{ mr: 1, fontSize: 20 }} />
          Assign to Collaborator
        </MenuItem>
        {selectedContent && selectedContent.assignedTo && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onReviewClick(selectedContent);
              setAnchorEl(null);
              setSelectedContentId(null);
              setSelectedContent(null);
            }}
          >
            <RateReview sx={{ mr: 1, fontSize: 20 }} />
            Review Content
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onContentClick(selectedContent);
            setAnchorEl(null);
            setSelectedContentId(null);
            setSelectedContent(null);
          }}
        >
          Edit / Preview
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(selectedContentId!);
            setAnchorEl(null);
            setSelectedContentId(null);
            setSelectedContent(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ContentCalendar;
